import { useLayoutEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CleaningToolsForm from "src/layout/CleaningToolsForm";
import SpecialAreas from "src/layout/SpecialAreas";
import DelicateObjects from "src/layout/DelicateObjects";
import { createInstructionsAPI } from "src/weirdStuff/api";
import { useConfirmation } from "src/context/confirmation";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";

const cleaningToolsSchema = yup.array().of(yup.string());

const filesSchema = yup
  .array()
  .of(yup.mixed().optional())
  .max(5, "Please upload at most 5 images or videos")
  .nullable()
  .optional();

const schema = yup.object().shape({
  cleaningTools: cleaningToolsSchema,
  cleaningInstructions: yup.string().when("cleaningTools", {
    is: (cleaningTools) => cleaningTools.includes("others"),
    then: yup.string().required("Please enter the name of the cleaning tool"),
    otherwise: yup.string().optional(),
  }),
  specialAreasFiles: filesSchema,
  specialAreasInstructions: yup
    .string()
    .max(1000, "Instructions cannot exceed 1000 characters"),
  delicateObjects: yup.array().of(yup.string()).optional(),
  delicateFiles: filesSchema,
  delicateInstructions: yup
    .string()
    .max(1000, "Instructions cannot exceed 1000 characters")
    .optional(),
  delicateOthers: yup.string().optional(),
});

const steps = {
  1: {
    title: "Cleaning tools",
    description:
      "Please select all the cleaning tools you already have in your home.",
    component: CleaningToolsForm,
    fields: ["cleaningTools", "cleaningInstructions"],
  },
  2: {
    title: "Special areas",
    description:
      "Upload images/videos and provide instructions for special areas.",
    component: SpecialAreas,
    fields: ["specialAreasFiles", "specialAreasInstructions"],
  },
  3: {
    title: "Delicate objects",
    description:
      "Tell us about delicate objects that need special care during cleaning.",
    component: DelicateObjects,
    fields: ["delicateObjects", "delicateFiles", "delicateInstructions"],
  },
};

function InstructionsForm({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(1);
  const { bookingId, instructionsToken } = useConfirmation();

  const StepComponent = steps[step].component;

  const form = useForm({
    defaultValues: {
      cleaningTools: [],
      cleaningInstructions: "",
      specialAreasFiles: [],
      specialAreasInstructions: "",
      delicateObjects: [],
      delicateFiles: [],
      delicateInstructions: "",
      delicateOthers: "",
    },
    resolver: yupResolver(schema),
  });

  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    console.log("data", data);
    const isValid = await form.trigger(steps[step].fields);
    console.log(form.formState.errors);
    if (isValid) {
      const isFinalStep = step === Object.keys(steps).length;
      if (isFinalStep) {
        setLoading(true);
        const input: any = data;
        input.bookingId = bookingId || "";
        input.instructionsToken = instructionsToken || "";
        createInstructionsAPI(input)
          .then((res) => {
            if (!res.error) {
              openNotificationWithIcon("success", "Success", res.message);
              onComplete();
            }
          })
          .catch((err) => {
            openNotificationWithIcon(
              "error",
              "Error creating instructions",
              err.message
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setStep((step) => Math.min(step + 1, Object.keys(steps).length));
      }
    }
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto" }}>
      {/* Step Progress Indicator */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "2rem",
          gap: "1rem",
        }}
      >
        {Object.keys(steps).map((stepNum) => (
          <div
            key={stepNum}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor:
                  parseInt(stepNum) <= step ? "var(--blue-300)" : "#e2e2e2",
                color: parseInt(stepNum) <= step ? "white" : "#666",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: "14px",
                cursor: loading ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (loading) return;
                setStep(parseInt(stepNum));
              }}
            >
              {stepNum}
            </div>
            {parseInt(stepNum) < Object.keys(steps).length && (
              <div
                style={{
                  width: "40px",
                  height: "2px",
                  backgroundColor:
                    parseInt(stepNum) < step ? "var(--blue-300)" : "#e2e2e2",
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Title and Description */}
      <div style={{ textAlign: "center", marginBottom: "3rem" }}>
        <h2 style={{ margin: "0 0 0.5rem 0", color: "var(--blue-300)" }}>
          Step {step}: {steps[step].title}
        </h2>
      </div>

      <FormProvider {...form}>
        <form
          id="instructions-form"
          method="post"
          encType="multipart/form-data"
        >
          <StepComponent
            onSubmit={form.handleSubmit(onSubmit)}
            isSubmitting={loading}
          />
        </form>
      </FormProvider>
    </div>
  );
}

export default InstructionsForm;
